import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { NextPage, GetServerSideProps } from 'next';
import Link from 'next/link';
import Image from 'next/image';
import { utils, Button, Link as StyledLink } from '@makeship/core';

import posthog from 'posthog-js';
import { S2, S3, H5, P1, P2, Caption, Overline, H5Styles } from '../../components/Typography';

import Head from '../../components/Head';
import Container from '../../components/Container';
import CountdownTimer from '../../components/CountdownTimer';
import ShareModal from '../../components/Modal/ShareModal';
import { IconWrapper } from './user/[username]';
import { getBadge } from '../../api/storefront/badge';
import { getProductByHandle } from '../../api/product';
import { getProductIDFromShopifyGID, parseGiftCardProductName, parseMetafields } from '../../utils/product';
import { ReactComponent as ShareIcon } from '../../../public/assets/icons/share-icon.svg';
import { ReactComponent as InfoIcon } from '../../../public/assets/icons/info-icon.svg';

import config from '../../../config.json';
import { isUserLoggedIn } from '../../utils/analytics';
import { useStore } from '../../store';
import { getPropsForPageLoad } from '../../utils/pageLoad';

const { routes } = config;

const BadgesPageWrapper = styled(Container)<{ needsPadding: boolean }>`
  padding: ${({ needsPadding }) => (needsPadding ? '48px 64px' : '8px 64px')};
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    padding: ${({ needsPadding }) => (needsPadding ? '48px 8px' : '8px')};
  }
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    padding: ${({ needsPadding }) => (needsPadding ? '16px 8px' : '8px')};
  }
`;
const BadgeHeader = styled.div`
  display: flex;
  align-items: flex-start;
`;

const TitleWrapper = styled.div`
  margin-bottom: 38px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    margin-bottom: 8px;
  }
`;

const Title = styled.h1`
  ${H5Styles};
  margin-bottom: 4px;
`;

const BadgeWrapper = styled.div`
  display: flex;
  flex-direction: row;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    flex-direction: column;
  }
`;

const Col = styled.div`
  flex: 1;
`;

const ImageWrapper = styled.div`
  max-width: 500px;
  margin: 0 auto;
  cursor: pointer;
`;

const BadgeInfoWrapper = styled.div`
  padding: 75px;
  text-align: center;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    padding: 0;
  }
`;

const BadgePosition = styled(H5)`
  margin-bottom: 16px;
`;

const ProductCreator = styled(S3)`
  text-transform: uppercase;
`;

const BadgeTitle = styled(H5)`
  margin-bottom: 24px;
  text-transform: uppercase;
`;

const TotalSoldWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 99px;
  margin-bottom: 24px;
`;

const TotalSold = styled(P1)`
  position: absolute;
  top: 48%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-transform: uppercase;
  white-space: nowrap;
  & > ${H5} {
    display: inline-block;
  }
`;

const CampaignDate = styled(Caption)`
  margin-bottom: 8px;
`;
const UnlockedDate = styled(P2)`
  color: ${({ theme }) => utils.hexToRGBA(theme.colors.neutral7, theme.alpha.dark)};
  margin-bottom: 36px;
`;

const InfoIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  cursor: pointer;
  position: relative;
`;

const ShareBadgeButton = styled(Button.Primary)`
  margin: 0 auto;
  margin-bottom: 24px;
`;

const Tooltip = styled.div`
  position: absolute;
  background-color: ${({ theme }) => theme.colors.neutral6};
  right: 0;
  top: 36px;
  width: 325px;
  padding: 8px 16px;
  border-radius: 3px;
  cursor: pointer;
  z-index: 2;
`;

const TooltipText = styled(P2)`
  color: ${({ theme }) => theme.colors.neutral1};
  & > span {
    color: ${({ theme }) => theme.colors.secondary};
  }
`;

const UnderBadgeText = styled(Overline)`
  color: ${({ theme }) => utils.hexToRGBA(theme.colors.neutral7, theme.alpha.dark)};
  text-align: center;
  margin-bottom: 8px;
`;

const BadgeCountdownTimerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

type TooltipContent = {
  name: string;
  description: string;
};

const tooltipContent: TooltipContent[] = [
  { name: 'White Badge', description: 'Awarded to all Supporters while a Campaign is still live.' },
  { name: 'Diamond Badge', description: 'First 100 Supporters of a successful Campaign.' },
  { name: 'Gold Badge', description: 'Supporters prior to achieving the funding goal.' },
  { name: 'Silver Badge', description: 'Supporters after achieving funding goal.' },
  { name: 'Gifting Badge', description: 'Awarded to Supporters who purchased a Plushie Gift for the community.' },
];

type BadgeTier = {
  [tier: number]: string;
};

const badgeTier: BadgeTier = {
  1: 'Diamond',
  2: 'Gold',
  3: 'Silver',
};

const renderCountdownTimer = (currentDate: string): React.ReactNode => (
  <BadgeCountdownTimerWrapper>
    <S2>Edition number revealed in</S2>
    <CountdownTimer date={currentDate} />
  </BadgeCountdownTimerWrapper>
);

type BadgeProps = {
  badge: Badge;
  product?: Shopify.Product | null;
};

const BadgesPage: NextPage<BadgeProps> = ({ badge, product }: BadgeProps) => {
  const { state } = useStore();
  const metafields = parseMetafields(product?.metafields);
  const [badgeSrc, setBadgeSrc] = useState(badge.badgeFrontUrl);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTooltipClicked, setIsTooltipClicked] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const handleClick = (e: MouseEvent) => {
    if (!!ref.current && !ref.current.contains(e.target as Node)) {
      setIsTooltipClicked(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const renderBadgeHeaderTitle = () =>
    product?.isGiftCard ? (
      <>{badge.customer?.username}&apos;s Gifting Badge</>
    ) : (
      <>
        {badge.customer?.username}&apos;s {badge.tier ? badgeTier[badge.tier] : 'White'} Badge
      </>
    );

  const handleBadgeClick = () => {
    setBadgeSrc(badgeSrc === badge.badgeFrontUrl ? badge.badgeBackUrl : badge.badgeFrontUrl);
    posthog.capture('badge_badge_click', {
      badge,
      product_id: getProductIDFromShopifyGID(product?.id || ''),
      is_logged_in: isUserLoggedIn(state.user),
    });
  };

  const handleInfoClick = () => {
    posthog.capture('badge_info_click', {
      badge,
      product_id: getProductIDFromShopifyGID(product?.id || ''),
      is_logged_in: isUserLoggedIn(state.user),
    });
    setIsTooltipClicked(true);
  };

  return (
    <>
      <Head
        title="Check out this Badge!"
        description="Support your favorite Content Creator and claim your Edition Badge from Makeship."
        url={`${routes.badges}/${badge.badgeId}`}
        type="badge"
        image={badge.badgeFrontUrl || ''}
      />
      <BadgesPageWrapper needsPadding={!!badge.tier}>
        {metafields.enddate && !badge.tier && renderCountdownTimer(metafields.enddate)}
        <BadgeHeader>
          {badge.customer?.username && (
            <TitleWrapper>
              <Title>{renderBadgeHeaderTitle()}</Title>
              <Link href={`${routes.badgeLibrary}/${encodeURIComponent(badge.customer?.username)}`} passHref>
                <StyledLink.Primary
                  onClick={() =>
                    posthog.capture('badge_viewBadgeLibrary_click', {
                      badge,
                      product_id: getProductIDFromShopifyGID(product?.id || ''),
                      is_logged_in: isUserLoggedIn(state.user),
                    })
                  }
                >
                  Visit Badge Library
                </StyledLink.Primary>
              </Link>
            </TitleWrapper>
          )}
          <InfoIconWrapper ref={ref} onClick={handleInfoClick}>
            <InfoIcon />
            {isTooltipClicked && (
              <Tooltip>
                {tooltipContent.map(({ name, description }) => (
                  <TooltipText key={name}>
                    <span>{name}:</span> {description}
                  </TooltipText>
                ))}
              </Tooltip>
            )}
          </InfoIconWrapper>
        </BadgeHeader>
        <BadgeWrapper>
          <Col>
            <ImageWrapper>
              <Image
                src={badgeSrc}
                layout="responsive"
                width="500px"
                height="500px"
                alt="badge image"
                onClick={handleBadgeClick}
                unoptimized
              />
            </ImageWrapper>
            <UnderBadgeText>Click badge to reverse</UnderBadgeText>
          </Col>
          <Col>
            <BadgeInfoWrapper>
              {!product?.isGiftCard && <BadgePosition>{badge.position ? `#${badge.position}` : '--'}</BadgePosition>}
              {metafields.creator && <ProductCreator>By: {metafields.creator}</ProductCreator>}
              <BadgeTitle>
                {!product?.isGiftCard ? badge.product.title : parseGiftCardProductName(badge.product.title)}
              </BadgeTitle>
              {!product?.isGiftCard && metafields.totalsold && (
                <TotalSoldWrapper>
                  <Image src="/assets/units-sold-background.png" width={240} height={99} />
                  <TotalSold>
                    only <H5>{metafields.totalsold}</H5> made
                  </TotalSold>
                </TotalSoldWrapper>
              )}
              {!product?.isGiftCard && <CampaignDate>{utils.getDateNoDay(badge.product.createdAt)}</CampaignDate>}
              <UnlockedDate>Unlocked {utils.getDate(badge.createdOn)}</UnlockedDate>
              <ShareBadgeButton
                onClick={() => {
                  setIsModalOpen(true);
                  posthog.capture('badge_shareBadge_click', {
                    badge,
                    product_id: getProductIDFromShopifyGID(product?.id || ''),
                    is_logged_in: isUserLoggedIn(state.user),
                  });
                }}
              >
                <IconWrapper>
                  <ShareIcon />
                </IconWrapper>
                Share Badge
              </ShareBadgeButton>
              {!product?.isGiftCard && (
                <Link href={`/products/${badge.product.handle}`} passHref>
                  <StyledLink.Primary
                    onClick={() => {
                      posthog.capture('badge_exploreProduct_click', {
                        badge,
                        product_id: getProductIDFromShopifyGID(product?.id || ''),
                        is_logged_in: isUserLoggedIn(state.user),
                      });
                    }}
                  >
                    Explore Product
                  </StyledLink.Primary>
                </Link>
              )}
            </BadgeInfoWrapper>
          </Col>
        </BadgeWrapper>
      </BadgesPageWrapper>
      <ShareModal
        isOpen={isModalOpen}
        closeModal={() => {
          setIsModalOpen(false);
        }}
        username={badge.customer?.username}
        badge={badge}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { params } = ctx;
  const badgeId = params ? (params.badgeId as string) : '';
  const badge = await getBadge(badgeId).then((res) => res.data);

  if (!badge) {
    return {
      notFound: true,
    };
  }

  const product = await getProductByHandle(badge.product.handle);
  const propsForPageLoad = await getPropsForPageLoad(ctx);

  return {
    props: {
      badge,
      product,
      ...propsForPageLoad,
    },
  };
};

export default BadgesPage;
